/**
 * BomAnalisiModal — analisi BOM + simulazione What-If per i costi di un'offerta.
 *
 * 3 colonne:
 *  Sinistra  — albero BOM stato attuale (giacenze, tempi, costi)
 *  Centro    — pannello What-If (variazioni % prezzi, tempi, ritardi)
 *  Destra    — grafico margine + riepilogo tempi (con scadenza e penali sopra)
 */
import { useState, useEffect } from 'react'
import { X, ChevronRight, ChevronDown, AlertTriangle, Package, Layers, Wrench, Clock, TrendingDown } from 'lucide-react'
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, ReferenceLine
} from 'recharts'
import api from '../../core/services/api'
import { CostoDiretto } from '../../types/offerte'

// ---- Tipi albero BOM ----

interface BomNode {
  articolo_id: string
  codice: string
  descrizione: string
  tipo: string
  unita_misura: string
  giacenza_attuale: number
  prezzo_acquisto: number
  quantita_richiesta: number
  ore_produzione: number
  costo_orario_produzione: number
  produzione_interna: boolean
  componenti: BomNode[]
  errore?: string
}

// ---- What-If: ogni materia prima/semilavorato ha un override ----

interface WhatIfOverride {
  articolo_id: string
  codice: string
  descrizione: string
  tipo: string
  prezzo_delta_pct: number   // +/- %
  ore_delta_pct: number      // +/- % tempi di produzione
  ritardo_gg: number         // giorni di ritardo fornitura
}

// ---- Helpers ----

function fmt(v: number) {
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(v)
}
function fmtH(h: number) {
  return h === 1 ? '1h' : `${h.toFixed(1)}h`
}

/** Calcola costo e ore totali di un nodo BOM ricorsivamente applicando gli override what-if.
 *  - Materie prime: costo = prezzo * qty, ore = 0
 *  - Semilavorati: ore di produzione solo per le unità mancanti in magazzino (qty - giacenza)
 *  - Prodotti finiti: ore di assemblaggio sempre sulla qty intera (sono oggetto dell'offerta)
 */
function calcNodoFixed(node: BomNode, qta_parent: number, overrides: Record<string, WhatIfOverride>): { costo: number; ore: number; ritardo_gg: number } {
  const qty = node.quantita_richiesta * qta_parent
  const ov = overrides[node.articolo_id]

  // Materia prima = foglia: nessuna produzione, solo prezzo e ritardo fornitura.
  // NON usare componenti.length === 0: anche un semilavorato senza materie prime
  // elencate ha componenti vuoti, ma ha ore di produzione proprie.
  if (node.tipo === 'materia_prima') {
    const prezzo = node.prezzo_acquisto * (1 + (ov?.prezzo_delta_pct ?? 0) / 100)
    const ritardo = ov?.ritardo_gg ?? 0
    return { costo: prezzo * qty, ore: 0, ritardo_gg: ritardo }
  }

  // Semilavorato o Prodotto Finito — può avere sub-componenti e/o ore di produzione proprie.
  let costoCom = 0
  let oreComp = 0
  let ritardoMax = 0
  for (const comp of node.componenti) {
    const r = calcNodoFixed(comp, qty, overrides)
    costoCom += r.costo
    oreComp += r.ore
    ritardoMax = Math.max(ritardoMax, r.ritardo_gg)
  }

  // Se non ha componenti elencati, il costo è il prezzo_acquisto del nodo stesso
  const costoBase = node.componenti.length > 0 ? costoCom : (node.prezzo_acquisto * qty)

  // Semilavorato: ore di produzione solo per le unità mancanti in magazzino
  // Prodotto finito: ore di assemblaggio sempre sull'intera qty (oggetto dell'offerta)
  const qtaDaProdurre = node.tipo === 'semilavorato'
    ? Math.max(0, qty - node.giacenza_attuale)
    : qty

  const oreMult = 1 + (ov?.ore_delta_pct ?? 0) / 100
  const oreAssembly = node.ore_produzione * oreMult * qtaDaProdurre
  const costoProd = oreAssembly * node.costo_orario_produzione
  oreComp += oreAssembly

  return { costo: costoBase + costoProd, ore: oreComp, ritardo_gg: ritardoMax }
}

/** Raccoglie tutte le foglie (materie prime) e nodi intermedi (semilavorati) unici per il pannello what-if */
function collectLeaves(node: BomNode, acc: Record<string, BomNode> = {}): Record<string, BomNode> {
  if (!acc[node.articolo_id]) {
    acc[node.articolo_id] = node
  }
  for (const c of node.componenti) {
    collectLeaves(c, acc)
  }
  return acc
}

// ---- Componente albero ----

function BomTree({ node, depth = 0, qta_mul = 1 }: { node: BomNode; depth?: number; qta_mul?: number }) {
  const [open, setOpen] = useState(depth < 2)
  const qty = node.quantita_richiesta * qta_mul
  const hasChildren = node.componenti.length > 0

  const tipoColor = node.tipo === 'materia_prima'
    ? 'text-amber-600'
    : node.tipo === 'semilavorato'
      ? 'text-blue-600'
      : 'text-green-600'

  const giacenzaOk = node.giacenza_attuale >= qty
  const giacenzaLabel = node.tipo === 'materia_prima'
    ? (giacenzaOk ? `Giacenza OK (${node.giacenza_attuale})` : `Da ordinare (giac. ${node.giacenza_attuale})`)
    : node.tipo === 'semilavorato'
      ? (giacenzaOk ? `Giacenza OK (${node.giacenza_attuale})` : `Da produrre (giac. ${node.giacenza_attuale})`)
      : undefined

  return (
    <div className={`${depth > 0 ? 'ml-4 border-l border-gray-200 pl-3' : ''}`}>
      <div
        className={`flex items-start gap-2 py-1.5 ${hasChildren ? 'cursor-pointer hover:bg-gray-50 rounded' : ''}`}
        onClick={() => hasChildren && setOpen(!open)}
      >
        {hasChildren ? (
          open ? <ChevronDown className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-gray-400" /> : <ChevronRight className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-gray-400" />
        ) : (
          <span className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-xs font-semibold font-mono ${tipoColor}`}>{node.codice}</span>
            <span className="text-xs text-gray-700 truncate">{node.descrizione}</span>
            <span className="text-xs text-gray-400">×{qty % 1 === 0 ? qty : qty.toFixed(2)}</span>
          </div>
          <div className="flex items-center gap-3 mt-0.5 flex-wrap">
            <span className="text-xs text-gray-500">€{node.prezzo_acquisto.toFixed(2)}/u</span>
            {node.ore_produzione > 0 && (
              <span className="text-xs text-indigo-500 flex items-center gap-0.5">
                <Clock className="w-3 h-3" />{fmtH(node.ore_produzione)} × {qty}
              </span>
            )}
            {giacenzaLabel && (
              <span className={`text-xs flex items-center gap-0.5 ${giacenzaOk ? 'text-green-600' : 'text-amber-600'}`}>
                {!giacenzaOk && <AlertTriangle className="w-3 h-3" />}
                {giacenzaLabel}
              </span>
            )}
          </div>
        </div>
      </div>
      {open && hasChildren && node.componenti.map(c => (
        <BomTree key={c.articolo_id} node={c} depth={depth + 1} qta_mul={qty} />
      ))}
    </div>
  )
}

// ---- Props ----

interface BomAnalisiModalProps {
  offertaId: string
  totaleValore: number          // ricavi offerta
  totaleCosti: number           // costi attuali offerta (da costiCalcolati)
  costiDiretti: CostoDiretto[]  // per filtrare prodotti finiti nel magazzino
  onClose: () => void
}

export function BomAnalisiModal({ totaleValore, totaleCosti, costiDiretti, onClose }: BomAnalisiModalProps) {
  const [trees, setTrees] = useState<Array<{ articolo_id: string; codice: string; descrizione: string; quantita: number; costoUnitarioOfferta: number; tree: BomNode }>>([])
  const [loading, setLoading] = useState(true)
  const [overrides, setOverrides] = useState<Record<string, WhatIfOverride>>({})

  // What-if pannello scadenza/penali
  const [scadenzaGg, setScadenzaGg] = useState('')
  const [penaleGgEuro, setPenaleGgEuro] = useState('')
  const [orePerGiorno, setOrePerGiorno] = useState('8')

  useEffect(() => {
    loadBom()
  }, [])

  const loadBom = async () => {
    setLoading(true)
    try {
      // Tutti i costi magazzino con articolo_id (qualsiasi tipo articolo)
      const magCosti = costiDiretti.filter(c => c.tipo_costo === 'magazzino' && c.articolo_id)
      if (magCosti.length === 0) { setLoading(false); return }

      // Fetch articoli per nome/codice/tipo
      const artRes = await api.get('/v1/magazzino/articoli', { params: { attivo: true } })
      const artMap: Record<string, any> = {}
      for (const a of artRes.data) artMap[a.id] = a

      // Dedup per articolo_id (stessa voce può essere in più costi)
      const visti = new Set<string>()
      const result: typeof trees = []

      for (const c of magCosti) {
        const artId = c.articolo_id!
        if (visti.has(artId)) continue
        visti.add(artId)

        const art = artMap[artId]
        // Tenta sempre il fetch BOM — l'endpoint restituisce 404 se non esiste
        try {
          const treeRes = await api.get(`/v1/magazzino/articoli/${artId}/bom-completa`)
          result.push({
            articolo_id: artId,
            codice: art?.codice ?? artId,
            descrizione: art?.descrizione ?? '',
            quantita: c.quantita ?? 1,
            costoUnitarioOfferta: Number(c.costo_unitario) || 0,
            tree: treeRes.data,
          })
        } catch { /* nessuna distinta per questo articolo — skip */ }
      }
      setTrees(result)

      // Inizializza overrides per tutte le foglie
      const allNodes: Record<string, BomNode> = {}
      for (const item of result) collectLeaves(item.tree, allNodes)
      const init: Record<string, WhatIfOverride> = {}
      for (const [id, node] of Object.entries(allNodes)) {
        init[id] = { articolo_id: id, codice: node.codice, descrizione: node.descrizione, tipo: node.tipo, prezzo_delta_pct: 0, ore_delta_pct: 0, ritardo_gg: 0 }
      }
      setOverrides(init)
    } finally {
      setLoading(false)
    }
  }

  // ---- Calcoli ----

  // ---- Calcoli BOM ----
  // Base: usa il costo_unitario congelato in offerta (non i prezzi attuali del DB)
  // Le ore vengono comunque calcolate dalla struttura BOM (non le storiciziamo)
  let costoBomBase = 0
  let oreBase = 0
  for (const item of trees) {
    costoBomBase += item.costoUnitarioOfferta * item.quantita
    const r = calcNodoFixed(item.tree, item.quantita, {})
    oreBase += r.ore
  }

  // What-if: con gli override attuali
  let costoBomWhatif = 0
  let oreWhatif = 0
  let ritardoMax = 0
  for (const item of trees) {
    const r = calcNodoFixed(item.tree, item.quantita, overrides)
    costoBomWhatif += r.costo
    oreWhatif += r.ore
    ritardoMax = Math.max(ritardoMax, r.ritardo_gg)
  }

  // Il costo what-if totale = totaleCosti + delta BOM
  // (i costi non-BOM restano invariati, il delta è l'unica variazione)
  const deltaBom = costoBomWhatif - costoBomBase
  const base = { costo: totaleCosti, ore: oreBase }
  const whatif = { costo: totaleCosti + deltaBom, ore: oreWhatif, ritardo_gg: ritardoMax }

  const margineBase = totaleValore - base.costo
  const margineWhatif = totaleValore - whatif.costo

  const scadGg = parseInt(scadenzaGg) || 0
  const penale = parseFloat(penaleGgEuro) || 0
  const oreGg = Math.max(1, parseInt(orePerGiorno) || 8)
  // Tempo totale: prima arrivano le forniture, poi si produce
  const giorniProduzione = Math.ceil(whatif.ore / oreGg)
  const giorniFornitura = whatif.ritardo_gg   // giorni attesa materiali (parallelo alla pianificazione)
  const totaleGiorni = giorniFornitura + giorniProduzione
  const ritardoConsegna = scadGg > 0 ? Math.max(0, totaleGiorni - scadGg) : 0
  const extraPenale = ritardoConsegna * penale
  const margineWhatifConPenale = margineWhatif - extraPenale

  const chartData = [
    {
      name: 'Attuale',
      Ricavi: totaleValore,
      Costi: base.costo,
      Margine: Math.max(0, margineBase),
    },
    {
      name: 'What-If',
      Ricavi: totaleValore,
      Costi: whatif.costo,
      Margine: Math.max(0, margineWhatifConPenale),
    },
  ]

  const allOverrideNodes = Object.values(overrides)
  const materiePrime = allOverrideNodes.filter(o => o.tipo === 'materia_prima')
  const semilavorati = allOverrideNodes.filter(o => o.tipo === 'semilavorato')

  const setOv = (id: string, field: keyof WhatIfOverride, val: number) => {
    setOverrides(prev => ({ ...prev, [id]: { ...prev[id], [field]: val } }))
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-7xl my-6">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <Layers className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-bold text-gray-900">Analisi BOM & Simulazione What-If</h2>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>

        {loading ? (
          <div className="p-12 text-center text-gray-500">Caricamento albero BOM...</div>
        ) : trees.length === 0 ? (
          <div className="p-12 text-center text-gray-400">
            <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>Nessun prodotto finito con distinta base trovato nei costi di questa offerta.</p>
            <p className="text-sm mt-1">Aggiungi prima dei materiali da magazzino di tipo <strong>Prodotto Finito</strong> con distinta base.</p>
          </div>
        ) : (
          <div className="grid grid-cols-3 divide-x divide-gray-200" style={{ minHeight: '600px' }}>

            {/* COLONNA 1 — Stato dell'arte */}
            <div className="p-5 overflow-y-auto max-h-[75vh]">
              <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-4 flex items-center gap-2">
                <Package className="w-4 h-4 text-gray-400" /> Stato Attuale
              </h3>
              {trees.map(item => (
                <div key={item.articolo_id} className="mb-6">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-mono text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">{item.codice}</span>
                    <span className="text-sm font-medium text-gray-800">{item.descrizione}</span>
                    <span className="text-xs text-gray-400">×{item.quantita}</span>
                  </div>
                  <BomTree node={item.tree} qta_mul={item.quantita} />
                  <div className="mt-2 text-xs text-gray-500 border-t border-gray-100 pt-2 flex gap-4">
                    <span>Costo in offerta: <strong className="text-gray-800">{fmt(item.costoUnitarioOfferta * item.quantita)}</strong></span>
                    <span>Ore: <strong>{fmtH(calcNodoFixed(item.tree, item.quantita, {}).ore)}</strong></span>
                  </div>
                </div>
              ))}

              {/* Riepilogo tempi per scaglione */}
              <div className="mt-4 bg-gray-50 rounded-lg p-3 text-xs space-y-1">
                <div className="font-semibold text-gray-600 mb-2">Riepilogo tempi (base)</div>
                {trees.map(item => {
                  const r = calcNodoFixed(item.tree, item.quantita, {})
                  return (
                    <div key={item.articolo_id} className="flex justify-between">
                      <span className="text-gray-600">{item.codice}</span>
                      <span className="font-medium">{fmtH(r.ore)}</span>
                    </div>
                  )
                })}
                <div className="flex justify-between border-t border-gray-200 pt-1 font-semibold">
                  <span>Totale</span>
                  <span className="text-indigo-700">{fmtH(oreBase)}</span>
                </div>
              </div>
            </div>

            {/* COLONNA 2 — What-If */}
            <div className="p-5 overflow-y-auto max-h-[75vh]">
              <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-4 flex items-center gap-2">
                <Wrench className="w-4 h-4 text-gray-400" /> Simulazione What-If
              </h3>

              {materiePrime.length > 0 && (
                <div className="mb-5">
                  <p className="text-xs font-semibold text-amber-700 uppercase mb-2">Materie Prime</p>
                  <div className="space-y-3">
                    {materiePrime.map(ov => (
                      <div key={ov.articolo_id} className="bg-amber-50 rounded-lg p-3">
                        <div className="text-xs font-medium text-gray-700 mb-2">{ov.codice} — {ov.descrizione}</div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-xs text-gray-500">Δ Prezzo %</label>
                            <div className="flex items-center gap-1 mt-0.5">
                              <input
                                type="number"
                                step="1"
                                value={ov.prezzo_delta_pct}
                                onChange={e => setOv(ov.articolo_id, 'prezzo_delta_pct', parseFloat(e.target.value) || 0)}
                                className="w-full px-2 py-1 text-xs border border-amber-200 rounded focus:outline-none focus:ring-1 focus:ring-amber-400 text-right"
                              />
                              <span className="text-xs text-gray-400">%</span>
                            </div>
                          </div>
                          <div>
                            <label className="text-xs text-gray-500">Ritardo fornitore (gg)</label>
                            <input
                              type="number"
                              step="1"
                              min="0"
                              value={ov.ritardo_gg}
                              onChange={e => setOv(ov.articolo_id, 'ritardo_gg', parseFloat(e.target.value) || 0)}
                              className="w-full mt-0.5 px-2 py-1 text-xs border border-amber-200 rounded focus:outline-none focus:ring-1 focus:ring-amber-400 text-right"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {semilavorati.length > 0 && (
                <div className="mb-5">
                  <p className="text-xs font-semibold text-blue-700 uppercase mb-2">Semilavorati</p>
                  <div className="space-y-3">
                    {semilavorati.map(ov => (
                      <div key={ov.articolo_id} className="bg-blue-50 rounded-lg p-3">
                        <div className="text-xs font-medium text-gray-700 mb-2">{ov.codice} — {ov.descrizione}</div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-xs text-gray-500">Δ Costo %</label>
                            <div className="flex items-center gap-1 mt-0.5">
                              <input
                                type="number"
                                step="1"
                                value={ov.prezzo_delta_pct}
                                onChange={e => setOv(ov.articolo_id, 'prezzo_delta_pct', parseFloat(e.target.value) || 0)}
                                className="w-full px-2 py-1 text-xs border border-blue-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-400 text-right"
                              />
                              <span className="text-xs text-gray-400">%</span>
                            </div>
                          </div>
                          <div>
                            <label className="text-xs text-gray-500">Δ Tempo prod. %</label>
                            <div className="flex items-center gap-1 mt-0.5">
                              <input
                                type="number"
                                step="1"
                                value={ov.ore_delta_pct}
                                onChange={e => setOv(ov.articolo_id, 'ore_delta_pct', parseFloat(e.target.value) || 0)}
                                className="w-full px-2 py-1 text-xs border border-blue-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-400 text-right"
                              />
                              <span className="text-xs text-gray-400">%</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {allOverrideNodes.length === 0 && (
                <p className="text-sm text-gray-400 text-center mt-8">Nessun componente BOM da variare.</p>
              )}

              {/* Riepilogo delta */}
              {allOverrideNodes.length > 0 && (
                <div className="mt-4 bg-gray-50 rounded-lg p-3 text-xs space-y-1">
                  <div className="font-semibold text-gray-600 mb-1">Impatto simulazione</div>
                  <div className="flex justify-between">
                    <span>Costo BOM base</span>
                    <span>{fmt(costoBomBase)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Costo BOM what-if</span>
                    <span className={costoBomWhatif > costoBomBase ? 'text-red-600 font-medium' : 'text-green-600 font-medium'}>
                      {fmt(costoBomWhatif)}
                    </span>
                  </div>
                  <div className="flex justify-between border-t border-gray-200 pt-1">
                    <span>Δ Costo totale</span>
                    <span className={deltaBom > 0 ? 'text-red-600 font-semibold' : deltaBom < 0 ? 'text-green-600 font-semibold' : 'text-gray-600 font-semibold'}>
                      {deltaBom >= 0 ? '+' : ''}{fmt(deltaBom)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Ore what-if</span>
                    <span>{fmtH(whatif.ore)}</span>
                  </div>
                  {whatif.ritardo_gg > 0 && (
                    <div className="flex justify-between text-amber-600">
                      <span>Ritardo max fornitura</span>
                      <span>+{whatif.ritardo_gg}gg</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* COLONNA 3 — Grafico margine */}
            <div className="p-5 overflow-y-auto max-h-[75vh]">
              <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-4 flex items-center gap-2">
                <TrendingDown className="w-4 h-4 text-gray-400" /> Margine & Tempi
              </h3>

              {/* Scadenza e penali */}
              <div className="bg-red-50 rounded-lg p-3 mb-4 space-y-2">
                <p className="text-xs font-semibold text-red-700 uppercase">Scadenza contrattuale & Penali</p>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="text-xs text-gray-600">Giorni disponibili</label>
                    <input
                      type="number" min="0" step="1"
                      value={scadenzaGg}
                      onChange={e => setScadenzaGg(e.target.value)}
                      placeholder="es. 30"
                      className="w-full mt-0.5 px-2 py-1 text-xs border border-red-200 rounded focus:outline-none focus:ring-1 focus:ring-red-400"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-600">Penale/giorno (€)</label>
                    <input
                      type="number" min="0" step="10"
                      value={penaleGgEuro}
                      onChange={e => setPenaleGgEuro(e.target.value)}
                      placeholder="es. 500"
                      className="w-full mt-0.5 px-2 py-1 text-xs border border-red-200 rounded focus:outline-none focus:ring-1 focus:ring-red-400"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-600">Ore lav./giorno</label>
                    <input
                      type="number" min="1" max="24" step="1"
                      value={orePerGiorno}
                      onChange={e => setOrePerGiorno(e.target.value)}
                      className="w-full mt-0.5 px-2 py-1 text-xs border border-red-200 rounded focus:outline-none focus:ring-1 focus:ring-red-400"
                    />
                  </div>
                </div>
                {/* Breakdown tempi */}
                {(whatif.ore > 0 || whatif.ritardo_gg > 0) && (
                  <div className="bg-white rounded p-2 text-xs space-y-1 border border-red-100">
                    {giorniFornitura > 0 && (
                      <div className="flex justify-between text-gray-600">
                        <span>Attesa forniture</span>
                        <span>{giorniFornitura} gg</span>
                      </div>
                    )}
                    {giorniProduzione > 0 && (
                      <div className="flex justify-between text-gray-600">
                        <span>Produzione ({fmtH(whatif.ore)} / {oreGg}h)</span>
                        <span>{giorniProduzione} gg</span>
                      </div>
                    )}
                    <div className="flex justify-between font-semibold border-t border-gray-100 pt-1 text-gray-800">
                      <span>Totale stimato (what-if)</span>
                      <span>{totaleGiorni} gg</span>
                    </div>
                    {scadGg > 0 && (
                      <div className={`flex justify-between font-semibold pt-1 ${ritardoConsegna > 0 ? 'text-red-600' : 'text-green-600'}`}>
                        <span>{ritardoConsegna > 0 ? `Ritardo previsto` : 'In tempo ✓'}</span>
                        <span>{ritardoConsegna > 0 ? `+${ritardoConsegna} gg` : `${scadGg - totaleGiorni} gg liberi`}</span>
                      </div>
                    )}
                  </div>
                )}
                {extraPenale > 0 && (
                  <div className="text-xs text-red-600 font-semibold flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    Penale stimata: {fmt(extraPenale)} ({ritardoConsegna}gg × {fmt(penale)}/gg)
                  </div>
                )}
              </div>

              {/* Grafico costi/ricavi */}
              <div className="mb-4">
                <p className="text-xs text-gray-500 mb-2 font-medium">Costi vs Ricavi</p>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={chartData} barGap={4}>
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `€${(v/1000).toFixed(0)}k`} />
                    <Tooltip formatter={(v: any) => fmt(v)} />
                    <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="Ricavi" fill="#22c55e" radius={[3,3,0,0]} />
                    <Bar dataKey="Costi" fill="#ef4444" radius={[3,3,0,0]} />
                    <Bar dataKey="Margine" fill="#3b82f6" radius={[3,3,0,0]} />
                    <ReferenceLine y={0} stroke="#6b7280" />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* KPI box */}
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-gray-50 rounded-lg p-3 text-center">
                    <p className="text-xs text-gray-500">Margine attuale</p>
                    <p className={`text-lg font-bold ${margineBase >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {fmt(margineBase)}
                    </p>
                    <p className="text-xs text-gray-400">
                      {totaleValore > 0 ? `${(margineBase / totaleValore * 100).toFixed(1)}%` : '—'}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 text-center">
                    <p className="text-xs text-gray-500">Margine what-if{extraPenale > 0 ? ' + penali' : ''}</p>
                    <p className={`text-lg font-bold ${margineWhatifConPenale >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {fmt(margineWhatifConPenale)}
                    </p>
                    <p className="text-xs text-gray-400">
                      {totaleValore > 0 ? `${(margineWhatifConPenale / totaleValore * 100).toFixed(1)}%` : '—'}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-indigo-50 rounded-lg p-3 text-center">
                    <p className="text-xs text-indigo-500 flex items-center justify-center gap-1">
                      <Clock className="w-3 h-3" /> Ore base
                    </p>
                    <p className="text-base font-bold text-indigo-700">{fmtH(oreBase)}</p>
                  </div>
                  <div className="bg-indigo-50 rounded-lg p-3 text-center">
                    <p className="text-xs text-indigo-500 flex items-center justify-center gap-1">
                      <Clock className="w-3 h-3" /> Ore what-if
                    </p>
                    <p className={`text-base font-bold ${whatif.ore > oreBase ? 'text-amber-600' : 'text-indigo-700'}`}>
                      {fmtH(whatif.ore)}
                    </p>
                  </div>
                </div>

                {margineWhatifConPenale < margineBase && (
                  <div className="flex items-center gap-2 bg-amber-50 rounded-lg p-2 text-xs text-amber-700">
                    <TrendingDown className="w-4 h-4 flex-shrink-0" />
                    Il margine peggiora di <strong>{fmt(margineBase - margineWhatifConPenale)}</strong> in questo scenario.
                  </div>
                )}
                {margineWhatifConPenale < 0 && (
                  <div className="flex items-center gap-2 bg-red-50 rounded-lg p-2 text-xs text-red-700">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                    <strong>Attenzione:</strong> in questo scenario l'offerta va in perdita.
                  </div>
                )}
              </div>
            </div>

          </div>
        )}
      </div>
    </div>
  )
}
